import React from 'react';
import { Code, Cloud, Cpu, Printer } from 'lucide-react';
import { useUserAuth } from '../../context/userAuthContext';

// Traducciones para el componente BenefitsSection
const translations = {
  en: {
    technologiesHeading: 'ENTERPRISE SOLUTIONS',
    whyChooseTitle: 'Strategic Technology Advantages',
    subheading: 'Implementing enterprise-grade technologies to drive innovation and efficiency',
    customSoftware: {
      title: 'Custom Software',
      description: 'Enterprise SAAS solutions engineered for specific organizational requirements'
    },
    cloudInfrastructure: {
      title: 'Cloud Infrastructure',
      description: 'Secure, high-performance cloud architecture with enterprise compliance'
    },
    hardwareIntegration: {
      title: 'Hardware Integration',
      description: 'End-to-end integration solutions connecting enterprise systems'
    },
    printing3D: {
      title: 'Additive Manufacturing',
      description: 'Industrial 3D printing capabilities for rapid prototyping and production'
    },
    readyToTransform: 'Accelerate your digital transformation',
    scheduleConsultation: 'Request a strategic technology assessment with our consulting team.',
    contactUs: 'Request Consultation'
  },
  es: {
    technologiesHeading: 'SOLUCIONES EMPRESARIALES',
    whyChooseTitle: 'Ventajas Tecnológicas Estratégicas',
    subheading: 'Implementación de tecnologías de nivel empresarial para impulsar la innovación y eficiencia',
    customSoftware: {
      title: 'Software Empresarial',
      description: 'Soluciones SAAS empresariales diseñadas para requisitos organizacionales específicos'
    },
    cloudInfrastructure: {
      title: 'Infraestructura Cloud',
      description: 'Arquitectura cloud segura de alto rendimiento con cumplimiento normativo'
    },
    hardwareIntegration: {
      title: 'Integración de Hardware',
      description: 'Soluciones de integración completas para sistemas empresariales'
    },
    printing3D: {
      title: 'Fabricación Aditiva',
      description: 'Capacidades industriales de impresión 3D para prototipado y producción'
    },
    readyToTransform: 'Acelere su transformación digital',
    scheduleConsultation: 'Solicite una evaluación tecnológica estratégica con nuestro equipo consultor.',
    contactUs: 'Solicitar Consulta'
  }
};

const BenefitsSection = () => {
  // Usar el contexto para obtener el idioma actual
  const { currentLanguage } = useUserAuth();
  const t = translations[currentLanguage];

  // Definir los beneficios con los colores exactos del logo
  const benefits = [
    {
      icon: <Code className="h-8 w-8 text-white" />,
      title: t.customSoftware.title,
      description: t.customSoftware.description,
      gradient: 'from-orange-500 to-orange-600', // Naranja del logo
      shadowColor: 'shadow-orange-200'
    },
    {
      icon: <Cloud className="h-8 w-8 text-white" />,
      title: t.cloudInfrastructure.title,
      description: t.cloudInfrastructure.description,
      gradient: 'from-blue-500 to-blue-600', // Azul del logo
      shadowColor: 'shadow-blue-200'
    },
    {
      icon: <Cpu className="h-8 w-8 text-white" />,
      title: t.hardwareIntegration.title,
      description: t.hardwareIntegration.description,
      gradient: 'from-teal-500 to-teal-600', // Verde turquesa del logo
      shadowColor: 'shadow-teal-200'
    },
    {
      icon: <Printer className="h-8 w-8 text-white" />,
      title: t.printing3D.title,
      description: t.printing3D.description,
      gradient: 'from-orange-400 to-orange-500', // Variación del naranja
      shadowColor: 'shadow-orange-200'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-20 w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Encabezado con estilo mejorado usando colores del logo */}
        <div className="mb-16 text-center">
          <div className="inline-block relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-blue-500 to-teal-500 rounded-lg blur opacity-20"></div>
            <div className="relative bg-white px-6 py-2 rounded-lg border border-gray-200">
              <h3 className="text-sm font-bold tracking-wider bg-gradient-to-r from-orange-500 via-blue-500 to-teal-500 bg-clip-text text-transparent uppercase">
                {t.technologiesHeading}
              </h3>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {t.whyChooseTitle}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.subheading}
          </p>
        </div>

        {/* Grid mejorado con gradientes del logo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
            >
              {/* Gradiente superior usando colores del logo */}
              <div className={`h-1 bg-gradient-to-r ${benefit.gradient}`}></div>

              {/* Contenido de la tarjeta */}
              <div className="p-8 relative">
                {/* Icono con gradiente del logo */}
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${benefit.gradient} shadow-lg ${benefit.shadowColor} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {benefit.icon}
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-gray-700 transition-colors">
                  {benefit.title}
                </h3>

                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>

                {/* Efecto de resplandor en hover */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 bg-gradient-to-br ${benefit.gradient} rounded-xl transition-opacity duration-300 pointer-events-none`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA con gradiente del logo */}
        <div className="mt-20 relative">
          {/* Fondo con gradiente del logo */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-blue-500 to-teal-500 rounded-2xl blur opacity-20"></div>

          <div className="relative bg-gradient-to-r from-orange-500 via-blue-500 to-teal-500 rounded-2xl overflow-hidden shadow-2xl">
            <div className="px-10 py-12 sm:px-12 flex flex-col sm:flex-row items-center justify-between">
              <div className="text-white mb-8 sm:mb-0 text-center sm:text-left">
                <h4 className="text-2xl font-bold mb-2">{t.readyToTransform}</h4>
                <p className="text-white/90 text-base max-w-lg leading-relaxed">
                  {t.scheduleConsultation}
                </p>
              </div>

              <div className="flex-shrink-0">
                <button className="group relative px-10 py-4 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105">
                  <span className="relative z-10">{t.contactUs}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-blue-500/10 to-teal-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenefitsSection;